import { ButtonGroupInputOption } from '@/components/form/baseInputs/inputTypes';
import { File05Icon } from '@/components/icons/File05Icon';
import { ListIcon } from '@/components/icons/ListIcon';
import { DocumentType } from '@/types/schema';

import { EntityType } from '../entities/types/EntityType';

// Map of document types to their display names
export const DOCUMENT_TYPE_DISPLAY_NAMES: Record<DocumentType, string> = {
  [DocumentType.AssetValuation]: 'Asset valuation',
  [DocumentType.SignedTrustDocument]: 'Trust document',
  [DocumentType.TrustAmendment]: 'Trust amendment',
  [DocumentType.TrustRelatedDocument]: 'Trust-related document',
  [DocumentType.CertificateOfTrust]: 'Certificate of trust',
  [DocumentType.TransferConfirmation]: 'Transfer confirmation',
  [DocumentType.BalanceSheet]: 'Balance sheet',
  [DocumentType.Will]: 'Will',
  [DocumentType.WillCodicil]: 'Will codicil',
  [DocumentType.PowerOfAttorney]: 'Power of attorney',
  [DocumentType.AssignmentOfInterest]: 'Assignment of interest',
  [DocumentType.AssignmentOfTangibleProperty]:
    'Assignment of tangible personal property',
  [DocumentType.LoanNoteAgreement]: 'Loan note agreement',
  [DocumentType.ArticlesOfIncorporation]: 'Articles of incorporation',
  [DocumentType.OperatingAgreement]: 'Operating agreement',
  [DocumentType.OperatingAgreementAmendment]: 'Operating agreement amendment',
  [DocumentType.InsurancePolicy]: 'Insurance policy',
  [DocumentType.InsurancePremiumPayment]: 'Insurance premium payment',
  [DocumentType.PartnershipAgreement]: 'Partnership agreement',
  [DocumentType.PartnershipAgreementAmendment]:
    'Partnership agreement amendment',
  [DocumentType.AccountDocumentationStatement]:
    'Account documentation statement',
  [DocumentType.TaxIdConfirmation]: 'Tax ID confirmation',
  [DocumentType.GiftTaxReturn]: 'Gift tax return',
  [DocumentType.IncomeTaxReturn]: 'Income tax return',
  [DocumentType.TaxReceipt]: 'Tax receipt',
  [DocumentType.CorporateBylaws]: 'Bylaws',
  [DocumentType.LlcAgreement]: 'LLC agreement',
  [DocumentType.LlcAgreementAmendment]: 'LLC agreement amendment',
  [DocumentType.ShareholdersAgreement]: 'Shareholders agreement',
  [DocumentType.StateBusinessFiling]: 'State business filing',
  [DocumentType.CrummeyLetter]: 'Crummey letter',
  [DocumentType.ExistingRemainderTrustDocument]: 'Remainder trust document',
  [DocumentType.GratDesignSummary]: 'GRAT design summary',
  [DocumentType.GeneratedPresentation]: 'Luminary presentation',
  [DocumentType.TaxFiling]: 'Tax filing',
  [DocumentType.DriversLicense]: `Driver's license/ID`,
  [DocumentType.Passport]: 'Passport',
  [DocumentType.BeneficialOwnershipInformationReport]:
    'Beneficial ownership information report',
  [DocumentType.FincenFiling]: 'FinCEN filing',
  [DocumentType.HealthcareProxy]: 'Health care proxy',
  [DocumentType.HipaaWaiver]: 'HIPAA waiver',
  [DocumentType.BeneficiaryDesignation]: 'Beneficiary designation',
  [DocumentType.LivingWill]: 'Living will',
  [DocumentType.Deed]: 'Deed',
  [DocumentType.LoggedContribution]: 'Logged contribution',
  [DocumentType.LoggedDistribution]: 'Logged distribution',
  [DocumentType.PrenuptialAgreement]: 'Prenuptial agreement',
  [DocumentType.DivorceSettlement]: 'Divorce settlement',
  [DocumentType.AdvanceDirective]: 'Advance directive',
  [DocumentType.LetterOfWishes]: 'Letter of wishes',
  [DocumentType.Binder]: 'Document binder',
  [DocumentType.GeneratedDocument]: 'Generated document',
  [DocumentType.HouseholdExport]: 'Household export',
  [DocumentType.Other]: 'Other',
};

const standardDocumentTypeOptions = [
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.AssetValuation],
    value: DocumentType.AssetValuation,
  },

  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.SignedTrustDocument],
    value: DocumentType.SignedTrustDocument,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.TrustAmendment],
    value: DocumentType.TrustAmendment,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.TrustRelatedDocument],
    value: DocumentType.TrustRelatedDocument,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.CertificateOfTrust],
    value: DocumentType.CertificateOfTrust,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.TransferConfirmation],
    value: DocumentType.TransferConfirmation,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.BalanceSheet],
    value: DocumentType.BalanceSheet,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.Will],
    value: DocumentType.Will,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.WillCodicil],
    value: DocumentType.WillCodicil,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.PowerOfAttorney],
    value: DocumentType.PowerOfAttorney,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.AssignmentOfInterest],
    value: DocumentType.AssignmentOfInterest,
  },
  {
    display:
      DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.AssignmentOfTangibleProperty],
    value: DocumentType.AssignmentOfTangibleProperty,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.LoanNoteAgreement],
    value: DocumentType.LoanNoteAgreement,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.ArticlesOfIncorporation],
    value: DocumentType.ArticlesOfIncorporation,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.OperatingAgreement],
    value: DocumentType.OperatingAgreement,
  },
  {
    display:
      DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.OperatingAgreementAmendment],
    value: DocumentType.OperatingAgreementAmendment,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.InsurancePolicy],
    value: DocumentType.InsurancePolicy,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.InsurancePremiumPayment],
    value: DocumentType.InsurancePremiumPayment,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.PartnershipAgreement],
    value: DocumentType.PartnershipAgreement,
  },
  {
    display:
      DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.PartnershipAgreementAmendment],
    value: DocumentType.PartnershipAgreementAmendment,
  },
  {
    display:
      DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.AccountDocumentationStatement],
    value: DocumentType.AccountDocumentationStatement,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.TaxIdConfirmation],
    value: DocumentType.TaxIdConfirmation,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.GiftTaxReturn],
    value: DocumentType.GiftTaxReturn,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.IncomeTaxReturn],
    value: DocumentType.IncomeTaxReturn,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.TaxReceipt],
    value: DocumentType.TaxReceipt,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.CorporateBylaws],
    value: DocumentType.CorporateBylaws,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.LlcAgreement],
    value: DocumentType.LlcAgreement,
  },

  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.LlcAgreementAmendment],
    value: DocumentType.LlcAgreementAmendment,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.ShareholdersAgreement],
    value: DocumentType.ShareholdersAgreement,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.StateBusinessFiling],
    value: DocumentType.StateBusinessFiling,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.CrummeyLetter],
    value: DocumentType.CrummeyLetter,
  },
  {
    display:
      DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.ExistingRemainderTrustDocument],
    value: DocumentType.ExistingRemainderTrustDocument,
    disabled: true, // can only create these document through the trust management interface
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.GratDesignSummary],
    value: DocumentType.GratDesignSummary,
    disabled: true, // can only create these document via generation
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.GeneratedPresentation],
    value: DocumentType.GeneratedPresentation,
    disabled: true, // can only create these document via generation
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.GeneratedDocument],
    value: DocumentType.GeneratedDocument,
    disabled: true, // can only create these document via generation
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.TaxFiling],
    value: DocumentType.TaxFiling,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.DriversLicense],
    value: DocumentType.DriversLicense,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.Passport],
    value: DocumentType.Passport,
  },
  {
    display:
      DOCUMENT_TYPE_DISPLAY_NAMES[
        DocumentType.BeneficialOwnershipInformationReport
      ],
    value: DocumentType.BeneficialOwnershipInformationReport,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.FincenFiling],
    value: DocumentType.FincenFiling,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.HealthcareProxy],
    value: DocumentType.HealthcareProxy,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.HipaaWaiver],
    value: DocumentType.HipaaWaiver,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.LivingWill],
    value: DocumentType.LivingWill,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.Deed],
    value: DocumentType.Deed,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.PrenuptialAgreement],
    value: DocumentType.PrenuptialAgreement,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.DivorceSettlement],
    value: DocumentType.DivorceSettlement,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.AdvanceDirective],
    value: DocumentType.AdvanceDirective,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.LetterOfWishes],
    value: DocumentType.LetterOfWishes,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.BeneficiaryDesignation],
    value: DocumentType.BeneficiaryDesignation,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.Binder],
    value: DocumentType.Binder,
  },
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.HouseholdExport],
    value: DocumentType.HouseholdExport,
  },
].sort((a, b) => {
  return a.display.localeCompare(b.display);
});

// we want OTHER to always be at the end, regardless of alphabetical order
export const DOCUMENT_TYPE_OPTIONS = [
  ...standardDocumentTypeOptions,
  {
    display: DOCUMENT_TYPE_DISPLAY_NAMES[DocumentType.Other],
    value: DocumentType.Other,
  },
];

export const DISABLED_DOCUMENT_TYPES = new Set(
  DOCUMENT_TYPE_OPTIONS.filter((option) => option.disabled).map(
    (option) => option.value
  )
);

export type DocumentDetailsViewOptions = 'summary' | 'document';

/**
 * Returns document details view button options with Summary button always enabled
 */
export function getDocumentDetailsViewButtonOptions(): ButtonGroupInputOption<DocumentDetailsViewOptions>[] {
  return [
    {
      display: 'Document',
      value: 'document',
      buttonProps: {
        startIcon: File05Icon,
      },
    },
    {
      display: 'Summary',
      value: 'summary',
      buttonProps: {
        startIcon: ListIcon,
      },
    },
  ];
}

// This is the tooltip text for the inputs where we allow users to associate documents with additional entities
export const ADDITIONAL_DOCUMENTS_POPPER_CONTENT =
  'Document will appear in specified entity’s documents list and can be referenced in document viewer when editing entity details';

export const ASSOCIATED_INDIVIDUAL_POPPER_CONTENT =
  'Document will no longer appear in the missing documents checklist for this individual.';
// Document type arrays for different entity categories
const TRUST_ENTITY_DOCUMENT_TYPES: DocumentType[] = [
  DocumentType.SignedTrustDocument,
  DocumentType.TrustAmendment,
  DocumentType.TrustRelatedDocument,
];

const BUSINESS_ENTITY_DOCUMENT_TYPES: DocumentType[] = [
  DocumentType.FincenFiling,
  DocumentType.TaxIdConfirmation,
  DocumentType.OperatingAgreement,
  DocumentType.OperatingAgreementAmendment,
  DocumentType.LlcAgreement,
  DocumentType.LlcAgreementAmendment,
  DocumentType.PartnershipAgreement,
  DocumentType.PartnershipAgreementAmendment,
  DocumentType.ShareholdersAgreement,
  DocumentType.AssignmentOfInterest,
];

const PERSONAL_ACCOUNT_DOCUMENT_TYPES: DocumentType[] = [];

/** Legal-document categories used as references for each entity type. */
export const ENTITY_REFERENCE_DOCUMENT_TYPES: Record<
  EntityType,
  DocumentType[]
> = {
  // Trust entity types
  qprt: TRUST_ENTITY_DOCUMENT_TYPES,
  grat: TRUST_ENTITY_DOCUMENT_TYPES,
  slat: TRUST_ENTITY_DOCUMENT_TYPES,
  ilit: TRUST_ENTITY_DOCUMENT_TYPES,
  'revocable-trust': TRUST_ENTITY_DOCUMENT_TYPES,
  'irrevocable-trust': TRUST_ENTITY_DOCUMENT_TYPES,
  clt: TRUST_ENTITY_DOCUMENT_TYPES,
  crt: TRUST_ENTITY_DOCUMENT_TYPES,

  // Business entity types
  'c-corp': BUSINESS_ENTITY_DOCUMENT_TYPES,
  gp: BUSINESS_ENTITY_DOCUMENT_TYPES,
  llc: BUSINESS_ENTITY_DOCUMENT_TYPES,
  lp: BUSINESS_ENTITY_DOCUMENT_TYPES,
  's-corp': BUSINESS_ENTITY_DOCUMENT_TYPES,
  'sole-proprietorship': BUSINESS_ENTITY_DOCUMENT_TYPES,

  // Personal account types - no documents for suggestions
  'custodial-account': PERSONAL_ACCOUNT_DOCUMENT_TYPES,
  daf: PERSONAL_ACCOUNT_DOCUMENT_TYPES,
  'individual-account': PERSONAL_ACCOUNT_DOCUMENT_TYPES,
  'insurance-account': PERSONAL_ACCOUNT_DOCUMENT_TYPES,
  'joint-account': PERSONAL_ACCOUNT_DOCUMENT_TYPES,
  'private-foundation': PERSONAL_ACCOUNT_DOCUMENT_TYPES,
  'qualified-tuition-account': PERSONAL_ACCOUNT_DOCUMENT_TYPES,
  'retirement-account': PERSONAL_ACCOUNT_DOCUMENT_TYPES,
};
